// resources/js/main.js
import App from '@/App.vue'
import { registerPlugins } from '@core/utils/plugins'
import { createApp } from 'vue'
import axios from './services/axios'
import { useSessionAuth } from './composables/useSessionAuth'

// Styles
import '@core-scss/template/index.scss'
import '@styles/styles.scss'
import '@styles/onboarding.scss'

// Initialize session auth helper
const { isAuthenticated } = useSessionAuth()

// Global error handler
window.addEventListener('error', event => {
  console.error('Global error:', event.error?.message || event.message)
  if (event.filename && event.filename.includes('/assets/')) {
    console.warn(`Asset loading error: ${event.filename}`)

    // Prevent default to avoid console spam for missing assets
    event.preventDefault()
  }
})

// Network error monitoring
window.addEventListener('unhandledrejection', event => {
  // Silence specific non-critical errors
  const errorMessage = event.reason?.message || String(event.reason)

  // List of errors to suppress (they don't break the app)
  const suppressedErrors = [
    'Failed to load',
    'Element not found',
    'Cannot read properties of null',
  ]

  const shouldSuppress = suppressedErrors.some(err => errorMessage.includes(err))

  if (shouldSuppress) {
    // Silently log as warning instead of error
    console.warn('Suppressed non-critical error:', errorMessage)
    event.preventDefault()
    
    return
  }

  console.error('Unhandled Promise rejection:', event.reason)
})

// Note: 401 handling is now done in the router guard before navigation
// and in the axios interceptor for API calls. We removed the global fetch
// interceptor to avoid race conditions and duplicate redirects.

// Initialize authentication and then create the app
async function initializeApp() {
  try {
    // Try to initialize authentication from session
    await isAuthenticated()
  } catch (error) {
    console.error('Authentication initialization error:', error)
  }

  // Create vue app
  const app = createApp(App)

  // Add Vue error handler
  app.config.errorHandler = (err, vm, info) => {
    console.error(`Vue error (${info}):`, err)

    // Allow app to continue despite errors
    return true
  }

  // Configure global axios defaults with correct base URL
  const apiBaseUrl = document.head.querySelector('meta[name="app-url"]')?.content || window.location.origin

  axios.defaults.baseURL = apiBaseUrl

  // Configure axios error handling
  axios.interceptors.response.use(
    response => response,
    error => {
      const url = error?.config?.url || ''
      const status = error?.response?.status
      const isOptionalHardwareQuickStatus404 = status === 404 && String(url).includes('/api/hardware-monitoring/quick-status')

      if (!isOptionalHardwareQuickStatus404)
        console.error('API Error:', error.message || 'Unknown error')
      
      return Promise.reject(error)
    },
  )

  // Custom asset loading handler
  const handleAssetError = () => {
    // Check if we can find the manifest file
    fetch('/build/manifest.json')
      .then(response => {
        if (!response.ok) {
          console.error('Cannot find manifest.json. Assets may be in the wrong location.')
        } else {
          console.log('Manifest file exists, but assets may be in a different path than expected.')
        }
      })
      .catch(() => {
        console.error('Cannot fetch manifest file. Check server configuration.')
      })
  }

  // Register plugins with error handling
  try {
    registerPlugins(app)
  } catch (error) {
    console.error('Plugin registration error:', error)
    
    // If the error is related to asset loading, show more details
    if (error.message && error.message.includes('Failed to load')) {
      handleAssetError()
    }
  }

  // Mount vue app
  try {
    app.mount('#app')
  } catch (error) {
    console.error('App mounting error:', error)
    
    // Provide fallback content if mounting fails
    const appEl = document.getElementById('app')
    if (appEl) {
      appEl.innerHTML = `
        <div style="padding: 20px; font-family: sans-serif;">
          <h2>Application Error</h2>
          <p>The application failed to load properly. This might be due to missing asset files.</p>
          <p>Error: ${error.message}</p>
          <p>Please check the console for more details.</p>
        </div>
      `
    }
  }
}

// Initialize the app
initializeApp()
